// web-staking-estimator-frontend/src/hooks/map/useAddressSearch.js
import { useState, useCallback } from "react";
import Point from "@arcgis/core/geometry/Point";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import { loadUiCfg } from "../../services/map/configService";

/**
 * Hook to handle address geocoding and map navigation
 */
export function useAddressSearch({ viewRef, onToast, runNearestForPoint, lastPointRef }) {
  const [searchText, setSearchText] = useState("");
  const [addressError, setAddressError] = useState(null);

  const geocodeAndDrop = useCallback(
    async (singleLine) => {
      if (!singleLine || !singleLine.trim()) return;
      const v = viewRef.current;
      if (!v) return;

      try {
        const url = new URL(
          "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates"
        );
        url.searchParams.set("f", "json");
        url.searchParams.set("singleLine", singleLine.trim());
        url.searchParams.set("outFields", "Match_addr,Addr_type");
        url.searchParams.set("maxLocations", "1");

        // Load config for searchExtent hint, bounds validation, and score threshold
        let ext = null;
        let mapCfg = null;
        try {
          const uiCfg = await loadUiCfg();
          mapCfg = uiCfg?.map;
          ext = mapCfg?.defaultExtent;
          if (ext && ext.xmin != null && ext.ymin != null && ext.xmax != null && ext.ymax != null) {
            const searchExtent = JSON.stringify({
              xmin: ext.xmin,
              ymin: ext.ymin,
              xmax: ext.xmax,
              ymax: ext.ymax,
              spatialReference: { wkid: ext.wkid || 4326 }
            });
            url.searchParams.set("searchExtent", searchExtent);
          }
        } catch (e) {
          console.warn("Could not load config for searchExtent:", e);
        }

        const r = await fetch(url.toString());
        if (!r.ok) throw new Error(`Geocode HTTP ${r.status}`);
        const json = await r.json();

        const best = Array.isArray(json?.candidates) ? json.candidates[0] : null;
        if (!best?.location) {
          setAddressError("No address found in our service area, please try again.");
          return;
        }

        // Validation 1: Score threshold (reject low-confidence matches)
        // Configurable via map.addressSearch.minScore, default 75
        const SCORE_THRESHOLD = mapCfg?.addressSearch?.minScore ?? 75;
        if (best.score != null && best.score < SCORE_THRESHOLD) {
          setAddressError("Address match quality too low, please provide a more specific address.");
          console.warn(`[AddressSearch] Score ${best.score} below threshold ${SCORE_THRESHOLD} for: ${singleLine}`);
          return;
        }

        // Validation 2: Bounds check (verify result is within service territory)
        if (ext && ext.xmin != null && ext.ymin != null && ext.xmax != null && ext.ymax != null) {
          const { x, y } = best.location;
          const withinBounds = (
            x >= ext.xmin &&
            x <= ext.xmax &&
            y >= ext.ymin &&
            y <= ext.ymax
          );

          if (!withinBounds) {
            setAddressError("Address is outside our service area, please try another location.");
            return;
          }
        }

        const pt4326 = new Point({
          x: best.location.x,
          y: best.location.y,
          spatialReference: { wkid: 4326 },
        });
        const ptWM = webMercatorUtils.geographicToWebMercator(pt4326);

        setAddressError(null);
        await v.goTo({ target: ptWM, zoom: 16 }, { duration: 600, easing: "ease" });
        if (lastPointRef) {
          lastPointRef.current = ptWM.clone();
        }
        await runNearestForPoint(ptWM, { emitSelectedPoint: true });
      } catch (err) {
        console.error("Address search error:", err);
        setAddressError("Address search failed. Please try again.");
      }
    },
    [viewRef, runNearestForPoint, lastPointRef]
  );

  const handleSearchTextChange = useCallback((val) => {
    setAddressError(null);
    setSearchText(val);
  }, []);

  const onSubmitSearch = useCallback(
    (e) => {
      e.preventDefault();
      const s = (searchText || "").trim();
      if (!s) return;
      geocodeAndDrop(s);
    },
    [searchText, geocodeAndDrop]
  );

  return {
    searchText,
    setSearchText: handleSearchTextChange,
    geocodeAndDrop,
    onSubmitSearch,
    addressError,
    clearAddressError: useCallback(() => setAddressError(null), []),
  };
}
